import React, { useState } from 'react'
import { Avatar, Card, Flex, List, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useCandidates } from '../../candidate/hooks'
import { CandidateQuery } from '../../candidate/candidate'
import { CandidateCompatibility } from '../../candidate/components/candidate-compatibility'
import { CandidateStatusTag } from '../../candidate/components/candidate-status-tag'
import { TimeAgo } from '../../components/time-ago'

type View = 'recent' | 'best'

/** The newest open candidates, or the open candidates who finished the interview, highest score first. */
const QUERIES: Record<View, CandidateQuery> = {
    recent: {
        include: ['campaign'],
        filters: {
            archived: [false],
        },
        sort: '-created_at',
        size: 10,
    },
    best: {
        include: ['campaign'],
        filters: {
            archived: [false],
            status: ['finished'],
        },
        sort: '-compatibility',
        size: 10,
    },
}

/**
 * The candidates on the dashboard: the most recent ones, and the best matches that are still open.
 */
export function DashboardCandidatesCard({ onOpenCandidate }: { onOpenCandidate: (candidateId: string) => void }) {
    const { t } = useTranslation()
    const [view, setView] = useState<View>('recent')
    const candidates = useCandidates({ query: QUERIES[view] })

    return (
        <Card
            className="w-full"
            activeTabKey={view}
            onTabChange={(key) => setView(key as View)}
            tabList={[
                {
                    key: 'recent',
                    label: t('Recent'),
                },
                {
                    key: 'best',
                    label: t('Best matches'),
                },
            ]}
            styles={{
                body: {
                    paddingTop: 10,
                    paddingBottom: 10,
                    paddingRight: 0,
                },
            }}
        >
            <List
                className="max-h-96 overflow-y-auto pr-6"
                dataSource={candidates.data?.data || []}
                loading={candidates.isFetching}
                renderItem={(candidate) => (
                    <List.Item
                        className="cursor-pointer"
                        onClick={() => onOpenCandidate(candidate.id)}
                    >
                        <List.Item.Meta
                            avatar={<Avatar src={candidate.avatar} />}
                            title={
                                <Flex
                                    className="w-full"
                                    align="center"
                                    justify="space-between"
                                >
                                    <Typography.Text
                                        ellipsis
                                        className="max-w-[160px]"
                                        title={candidate.name}
                                    >
                                        {candidate.name}
                                    </Typography.Text>
                                    <CandidateCompatibility candidate={candidate} />
                                </Flex>
                            }
                            description={
                                <Flex
                                    vertical
                                    className="-mt-0.5"
                                    gap="small"
                                >
                                    <Typography.Text
                                        type="secondary"
                                        className="text-xs"
                                    >
                                        {candidate.campaign?.title}
                                    </Typography.Text>
                                    <Flex
                                        align="center"
                                        gap="small"
                                    >
                                        <CandidateStatusTag status={candidate.status} />
                                        <Typography.Text
                                            type="secondary"
                                            className="text-xs"
                                        >
                                            <TimeAgo date={candidate.createdAt} />
                                        </Typography.Text>
                                    </Flex>
                                </Flex>
                            }
                        />
                    </List.Item>
                )}
            />
        </Card>
    )
}
